import React, { FormEvent, useState } from 'react';
import { AlertCircle, Loader2, MailCheck } from 'lucide-react';
import { RouteLink } from '../../lib/router';
import { sendMagicLink, SupabaseError } from '../../lib/supabase';
import { AdminField, adminInputClass } from './AdminField';
interface AdminSignInProps {
  linkError: string | null;
}
type Phase = 'idle' | 'sending' | 'sent';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function describeSendError(error: unknown): string {
  if (error instanceof SupabaseError) {
    if (error.status === 0) return 'Couldn’t reach the server. Check your connection and try again.';
    if (error.status === 429) {
      return 'Too many sign-in emails in a short time. Wait a minute, then try again.';
    }
    if (/signup|not allowed|not found/i.test(error.message) || error.status === 422) {
      return 'That email isn’t set up to edit events. Check the spelling, or ask to be added.';
    }
  }
  return 'The sign-in link couldn’t be sent. Please try again.';
}
export function AdminSignIn({
  linkError
}: AdminSignInProps) {
  const [email, setEmail] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_PATTERN.test(trimmed)) {
      setError('Enter the email address you use for Pines Retreat.');
      return;
    }
    setPhase('sending');
    setError(null);
    try {
      await sendMagicLink(trimmed, `${window.location.origin}/admin`);
      setPhase('sent');
    } catch (caught) {
      setError(describeSendError(caught));
      setPhase('idle');
    }
  };
  return <main className="flex min-h-screen w-full items-center justify-center bg-cream px-6 py-16" data-id="element-44" data-mp="2yeqm4m7rzcxl">
      <div className="w-full max-w-md" data-id="element-45" data-mp="2tf484ak4hsnq">
        <RouteLink to="/" className="mb-10 inline-block font-serif text-xl text-pine transition-colors hover:text-ember" data-id="element-46" data-mp="2te2vi1ngky3w">
          Pines Retreat
        </RouteLink>

        {phase === 'sent' ? <div className="rounded-sm border border-pine/15 bg-creamDeep p-8" data-id="element-47" data-mp="2jr8p8a0gfmlq">
            <MailCheck className="mb-5 h-7 w-7 text-ember" aria-hidden="true" data-id="element-48" data-mp="2jp5p2x5l8d3a" />
            <h1 className="mb-3 font-serif text-3xl text-pine" data-id="element-49" data-mp="25t1yddtxamvk">Check your email</h1>
            <p className="mb-6 font-light leading-relaxed text-clay" aria-live="polite" data-id="element-50" data-mp="29u3zk07eh906">
              We sent a sign-in link to <span className="font-medium text-pine" data-id="element-51" data-mp="2xj6qjynxce7c">{email.trim()}</span>.
              Open it on the device you want to edit from.
            </p>
            <button type="button" onClick={() => setPhase('idle')} className="text-sm font-medium text-pine underline decoration-pine/30 underline-offset-4 transition-colors hover:text-ember hover:decoration-ember" data-id="element-52" data-mp="2oql9cmzxyfkm">
              Use a different email
            </button>
          </div> : <div className="rounded-sm border border-pine/15 bg-creamDeep p-8" data-id="element-53" data-mp="3l9iw52vp5yur">
            <h1 className="mb-3 font-serif text-3xl text-pine" data-id="element-54" data-mp="19rxv2df8zypz">Events editor</h1>
            <p className="mb-7 font-light leading-relaxed text-clay" data-id="element-55" data-mp="14uxn1wyu8lw8">
              Enter your email and we’ll send you a link to sign in. No password needed.
            </p>

            {linkError && <div className="mb-6 flex gap-3 rounded-sm border border-ember/30 bg-cream p-4 text-sm text-clay" data-id="element-56" data-mp="1el0ehsqb9rsg">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-none text-ember" aria-hidden="true" data-id="element-57" data-mp="qv64rk1agjcf" />
                <p data-id="element-58" data-mp="lwlcc6sa33yj">{linkError}</p>
              </div>}

            <form onSubmit={handleSubmit} noValidate data-id="element-59" data-mp="10t3s85bbqc3z">
              <AdminField label="Email" htmlFor="admin-email" error={error ?? undefined} data-id="element-60" data-mp="10qhjw3glzskp">
                <input id="admin-email" type="email" autoComplete="email" inputMode="email" value={email} onChange={event => {
              setEmail(event.target.value);
              if (error) setError(null);
            }} aria-invalid={Boolean(error)} aria-describedby={error ? 'admin-email-error' : undefined} className={adminInputClass} placeholder="you@example.com" disabled={phase === 'sending'} data-id="element-61" data-mp="vs699qihpe1f" />
              </AdminField>
              <button type="submit" disabled={phase === 'sending'} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-sm bg-pine px-6 py-3.5 font-medium tracking-wide text-cream transition-colors hover:bg-pineDeep disabled:cursor-not-allowed disabled:opacity-70" data-id="element-62" data-mp="zr6jth9ahbz3">
                {phase === 'sending' && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-63" data-mp="1ih4u840k7h5w" />}
                {phase === 'sending' ? 'Sending link…' : 'Email me a sign-in link'}
              </button>
            </form>
          </div>}
      </div>
    </main>;
}